import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { inputCls, btnPrimary, Label } from "./PortalUI";

const KINDS = [
  { v: "all", l: "All enrolled students" },
  { v: "program", l: "All students in a program" },
  { v: "cohort", l: "A class or cohort" },
  { v: "instructor", l: "An instructor's roster" },
  { v: "mentor", l: "A mentor's roster" },
  { v: "students", l: "Individual student(s)" },
];

export const AssignDialog = ({ open, onClose, endpoint, title, showDue = false, onDone }) => {
  const [opts, setOpts] = useState(null);
  const [kind, setKind] = useState("students");
  const [value, setValue] = useState("");
  const [ids, setIds] = useState([]);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && !opts) api.get("/admin/portal-options").then((r) => setOpts(r.data)).catch(() => {}); }, [open, opts]);

  const choices = { program: opts?.programs?.map((p) => ({ v: p, l: p })), cohort: opts?.cohorts?.map((c) => ({ v: c, l: c })),
    instructor: opts?.instructors?.map((i) => ({ v: i.email, l: `${i.name} (${i.email})` })), mentor: opts?.mentors?.map((i) => ({ v: i.email, l: `${i.name} (${i.email})` })) }[kind];

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(endpoint, { target: { kind, value: value || null, student_ids: ids }, due_date: due || null });
      toast.success(`Assigned to ${r.data.assigned} student(s)${r.data.skipped_existing ? ` · ${r.data.skipped_existing} already assigned` : ""}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-navy">{title || "Assign"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Assign to</Label>
            <select value={kind} onChange={(e) => { setKind(e.target.value); setValue(""); }} className={inputCls} data-testid="assign-kind">
              {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
            </select></div>
          {choices && (
            <div><Label>Select</Label>
              <select value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} data-testid="assign-value">
                <option value="">— choose —</option>
                {choices.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select></div>
          )}
          {kind === "students" && (
            <div className="max-h-56 overflow-y-auto border border-navy/10 rounded-md divide-y divide-navy/5" data-testid="assign-student-list">
              {(opts?.students || []).length === 0 && <p className="text-sm text-dark-text/50 p-3">No enrolled students yet.</p>}
              {(opts?.students || []).map((s) => (
                <label key={s.student_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-soft-gray">
                  <input type="checkbox" className="accent-magenta" checked={ids.includes(s.student_id)} data-testid={`assign-student-${s.student_id}`}
                    onChange={(e) => setIds(e.target.checked ? [...ids, s.student_id] : ids.filter((x) => x !== s.student_id))} />
                  <span className="font-semibold text-navy">{s.name}</span><span className="text-dark-text/50 text-xs">{s.email}</span>
                </label>
              ))}
            </div>
          )}
          {showDue && <div><Label>Due date (optional — overrides document default)</Label>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} data-testid="assign-due" /></div>}
          <button onClick={submit} disabled={busy || (kind === "students" ? !ids.length : (choices && !value))} className={`${btnPrimary} w-full`} data-testid="assign-submit">
            {busy ? "Assigning…" : "Assign"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
